const Backbone = require('backbone');
const FacetItemModel = require('app/models/store/client/facets/facet_item_model');
const FacetGroupModel = require('app/models/store/client/facets/facet_group_model');
const FacetHelper = require('lib/helpers/facet_helper');
const FacetItemCollection = Backbone.Collection.extend({
	model: FacetItemModel
});

const FacetGroupCollection = Backbone.Collection.extend({
	api: 'local',
	productUrl: '/app/api/search/products?',
	contentUrl: '/app/api/search/content?',
	facetIdPrefix: 'f',

	model: FacetGroupModel,

	initialize(facetGroups, options) {
		this.options = options;

		// *** for each FacetGroupModel, sets a nested FacetItemCollection (using resultValues) and listens to it.
		this._setEachGroupFacetItemCollection(facetGroups);

		// *** Some events available to whichever views are using this collection
		this.on('facets:items:clearall', this._onFacetItemsClearAll, this);
		this.on('facets:items:set', this._onFacetItemsSet, this);
		this.on('facets:items:rangeset', this._onFacetItemRangeSet, this);
		this.on('facets:groups:collapse', this._collapseFacetGroups, this);
		this.on('facets:fetch:results', this._setCollectionFromProductDropResults, this);

		// *** Keep things clean...
		this.on('remove', this._onModelRemove, this);
	},
	/**
	 * @private
	 * @param {*} facetGroupModel
	 * @return {void}
	 */
	_onModelRemove(facetGroupModel) {
		if (facetGroupModel && facetGroupModel.destroy) {
			facetGroupModel.destroy();
		}
	},
	/**
	 * @private
	 * @param {*} facetItems
	 * @return {*}
	 */
	_getNewFacetItemCollection(facetItems) {
		const facetItemCollection = new FacetItemCollection(facetItems);
		this.listenTo(facetItemCollection, 'change:selected', this._onFacetItemModelChange.bind(this));
		return facetItemCollection;
	},
	/**
	 * @private
	 * @template T
	 * @param {T} groupName
	 * @return {{groupName: T; [key: string]: any} | undefined}
	 */
	_getFacetGroupModel(groupName) {
		return this.findWhere({ groupName });
	},
	/**
	 * @private
	 * @template T
	 * @param {*} groupName
	 * @param {T} value
	 * @return {{value: T; [key: string]: any} | undefined | null}
	 */
	_getFacetItemByGroupAndValue(groupName, value) {
		const facetGroup = this._getFacetGroupModel(groupName);
		return facetGroup ? facetGroup.get('facetItemCollection').findWhere({ value }) : null;
	},
	/**
	 * @private
	 * @template T
	 * @param {number} facetId
	 * @param {T} value
	 * @return {{value: T; [key: string]: any} | undefined | null}
	 */
	_getFacetItemByIdAndValue(facetId, value) {
		const facetGroup = this.findWhere({ facetId });
		return facetGroup ? facetGroup.get('facetItemCollection').findWhere({ value }) : null;
	},
	/**
	 * @private
	 * @param {*} groupName
	 * @return {void}
	 */
	_collapseFacetGroups(groupName) {
		if (groupName) {
			const facetGroupModel = this._getFacetGroupModel(groupName);
			facetGroupModel.set('isCollapsed', !facetGroupModel.get('isCollapsed'));
		} else {
			this.each((facetGroupModel) => {
				facetGroupModel.trigger('external:collapse');
			});
		}
	},
	/**
	 * @private
	 * @param {{get: Function; [key: string]: any}} facetItemModel
	 * @return {void}
	 */
	_onFacetItemModelChange(facetItemModel) {
		const groupName = facetItemModel.get('groupName');
		this.trackFacetChangeInOmniture(
			groupName,
			facetItemModel.get('value'),
			facetItemModel.get('selected'),
			facetItemModel.get('trackingString')
		);
		this._updateFacetGroupWithSelectedFacetItem(this._getFacetGroupModel(groupName), facetItemModel);

		this._throttleChangeEvent();
	},
	/**
	 * @private
	 * @param {{get: Function; set: Function; [key: string]: any}} facetGroupModel
	 * @param {{get: Function; [key: string]: any}} facetItemModel
	 * @return {void}
	 */
	_updateFacetGroupWithSelectedFacetItem(facetGroupModel, facetItemModel) {
		let selectedFacetQueries = facetGroupModel.get('selectedFacetQueries'),
			selectedValues = facetGroupModel.get('selectedValues').split(',');

		const facetQuery = facetItemModel.get('facetQuery'),
			facetValue = facetItemModel.get('value');

		if (facetItemModel.get('selected')) {
			selectedFacetQueries.push(facetQuery);
			selectedValues.push(facetValue);
		} else {
			selectedFacetQueries = selectedFacetQueries.filter((fc) => fc !== facetQuery);
			selectedValues = selectedValues.filter((val) => val !== facetValue);
		}
		// loop over selectedFacetModels and to ensure that a facetItem exists that has a count greater than 0
		const currentFacetModels = facetGroupModel.get('facetItemCollection').models;
		if (currentFacetModels) {
			let hasNonZeroCountFacetItem = currentFacetModels.some((facetItem) => {
				const { attributes } = facetItem;
				return Boolean(attributes.selected && attributes.count);
			});
			// If none of our selected facets have product then we want to clear the selected facets
			if (!hasNonZeroCountFacetItem) {
				selectedFacetQueries = [];
				selectedValues = [];
			}
		}
		facetGroupModel.set({
			selected: selectedFacetQueries.length > 0,
			selectedValues: selectedValues.join(', '),
			selectedFacetQueries: selectedFacetQueries
		});
	},
	/**
	 * @private
	 * @return {void}
	 */
	_onFacetItemsClearAll() {
		const selectedFacetGroupModels = this.where({ selected: true });
		if (selectedFacetGroupModels.length) {
			selectedFacetGroupModels.forEach((facetGroupModel) => {
				facetGroupModel.get('facetItemCollection').each((facetItemModel) => {
					facetItemModel.set({ selected: false });
				});
			});
		} else {
			// When there is a "no results" outcome and user needs to reset the view.
			this._throttleChangeEvent();
		}
	},
	/**
	 * @private
	 * @param {{ facetId, groupName, facetValue, selected, trackingString }} facetItemData
	 * @param {Function} onError
	 * @return {void}
	 */
	_onFacetItemsSet(facetItemData, onError) {
		const { facetId, groupName, facetValue, selected, trackingString } = facetItemData;
		let facetItem = this._getFacetItemByGroupAndValue(groupName, facetValue) || this._getFacetItemByIdAndValue(facetId, facetValue);
		if (!facetItem) {
			onError && onError();
			return;
		}

		facetItem.set({ selected, trackingString });
		this._setMostRecentTrackingString(trackingString);
	},
	/**
	 * @private
	 * @param {{facetId, groupName, selectedMin, selectedMax, selected, isAdditive, trackingString }} facetItemData
	 * @param {Function} onError
	 * @return {void}
	 */
	_onFacetItemRangeSet(facetItemData, onError) {
		const { facetId, groupName, selectedMin, selectedMax, selected, isAdditive, trackingString } = facetItemData;
		const facetGroupModel = this._getFacetGroupModel(groupName) || this.findWhere({ facetId }),
			rangeFacetItemModel = facetGroupModel && facetGroupModel.get('facetItemCollection').first();
		if (!rangeFacetItemModel) {
			onError && onError();
			return;
		}
		const facetIdStr = `f${rangeFacetItemModel.get('facetId')}`;

		facetGroupModel.set({ selectedMin, selectedMax });
		if (!isAdditive) {
			facetGroupModel.set({ selectedValues: '', selectedFacetQueries: [] });
		}
		rangeFacetItemModel.set({
			value: `${selectedMin} - ${selectedMax}`,
			facetQuery: `${facetIdStr}=${selectedMin}&${facetIdStr}=${selectedMax}`,
			trackingString: trackingString || ''
		});
		this._setMostRecentTrackingString(trackingString);
		// As soon as slider changes, selected will almost always be true, which won't retrigger our 'change:selected' watches.
		// More reliable to just set silent true, and trigger manually.
		rangeFacetItemModel.set({ selected: selected }, { silent: true });
		this._onFacetItemModelChange(rangeFacetItemModel);
	},
	/**
	 * @private
	 * @template {{resultValues: Array; groupName: string; facetItemCollection?: any; facetId?: number}} T
	 * @param {T[]} facetGroups
	 * @return {T[]}
	 */
	_setEachGroupFacetItemCollection(facetGroups) {
		return facetGroups.map((facetGroup) => {
			const facetItems = facetGroup.resultValues.map((facet) => Object.assign(facet, { groupName: facetGroup.groupName }));
			facetGroup.facetItemCollection = this._getNewFacetItemCollection(facetItems);
			facetGroup.facetId = facetGroup.resultValues[0].facetId;
			return facetGroup;
		});
	},
	/**
	 * @private
	 * @param {*} results
	 * @return {void}
	 */
	_setCollectionFromProductDropResults(results) {
		this.set(this.parse(results));
	},
	/**
	 * @private
	 * @param {string} trackingString
	 */
	_setMostRecentTrackingString(trackingString) {
		// facet_banners have intcmp tracking we need to persist to views which set window.location things.
		this.trackingString = trackingString;
	},

	/**
	 * Multiple facetItems can be selected/deselected at the same time in some cases.
	 * e.g. facet banners and any "clear all" actions.  This throttle keeps a bunch
	 * of extra api calls from being triggered, (one for each individual FacetItem change),
	 * when we only need the last one.
	 * @private
	 * @return {void}
	 */
	_throttleChangeEvent() {
		if (this.fetchTimeout) {
			window.clearTimeout(this.fetchTimeout);
		}
		this.fetchTimeout = setTimeout(
			function() {
				this.trigger('facets:collection:change');
			}.bind(this),
			40
		);
	},

	/**
	 * ==================================================================================
	 * ************************ PUBLIC METHODS ************************************
	 * ==================================================================================
	 */

	/**
	 * @return {Array}
	 */
	getSelectedFacets() {
		let selectedFacets = [];
		this.where({ selected: true }).forEach((facetGroupModel) => {
			const facetItemModels = facetGroupModel.get('facetItemCollection').toJSON();
			if (facetItemModels && facetItemModels.length) {
				selectedFacets = selectedFacets.concat(facetItemModels.filter((f) => f.selected));
			}
		});
		return selectedFacets;
	},
	/**
	 * @return {number}
	 */
	getSelectedFacetItemsCount() {
		let itemsCount = 0;
		this.where({ selected: true }).forEach((item) => {
			let value = item.attributes.selectedCount;
			itemsCount += value;
		});
		return itemsCount;
	},
	/**
	 * @return {Array}
	 */
	getFacetQueries() {
		const selectedFacetQueries = this.pluck('selectedFacetQueries').filter((arr) => arr.length);
		return [].concat.apply([], selectedFacetQueries);
	},
	/**
	 * @return {string}
	 */
	getFacetTrackingString() {
		return this.trackingString || '';
	},
	/**
	 * @param {string | number} groupName
	 * @param {string | number} facetValue
	 * @param {boolean} isSelected
	 * @param {string} trackingString
	 * @return {void}
	 */
	trackFacetChangeInOmniture: function(groupName, facetValue, isSelected, trackingString) {
		const dl = window.dataLayer;

		let trimmedFacetValue = (facetValue || '')
				.toString()
				.trim()
				.toLowerCase(),
			trimmedGroupName = (groupName || '')
				.toString()
				.trim()
				.toLowerCase();

		Backbone.trigger('omniture:facetChanged', {
			isAdd: isSelected,
			facetValue: trimmedFacetValue,
			groupName: trimmedGroupName,
			categoryId: dl.categoryId,
			searchTerm: dl.search ? dl.search.name : undefined,
			trackingString: trackingString
		});
	},
	/**
	 * @param {Object} response
	 * @return {{resultValues: Array; groupName: string; facetItemCollection?: any;}}
	 */
	parse(response = {}) {
		FacetHelper.mergeSelectedFacets(response);
		this.info = response.info || { count: 0 };
		return this._setEachGroupFacetItemCollection(response.facets || []);
	},
	/**
	 * @return {string}
	 */
	url() {
		const dl = window.dataLayer,
			isProductUrl = Boolean(dl.categoryId || dl.search || dl.isPla),
			url = isProductUrl ? this.productUrl : this.contentUrl;
		return url + this.options.getQueryParams().join('&');
	}
});

module.exports = FacetGroupCollection;
